import { jwtDecode } from 'jwt-decode';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { setCurrentQuestion } from '../../../redux/Slices/QuestionsSlice';
import {
  setFilters,
  setIsTestInstructionsOpen,
  setIsTestStarted,
  setQid,
  setResetFilters,
  setResultByIdTestDTO,
  setTestDetailsUnavailable
} from '../../../redux/Slices/StartTestSlice/startFulTest';
import { RootState } from '../../../redux/store';
import { useAppSelector } from '../../../redux/store/hooks';
import { useFetchCloudPrepUserTestQuery } from '../../../services/cloudPrepApi';
import { useLazyGetWrittenTestFullDetailsQuery } from '../../../services/startTestAPI/resultsTestApi';
import {
  useFetchMockTestResulsQuery,
  useGetAllSectionsDataQuery,
  useGetTestResultByIdQuery,
  useLazyGetTestResultByIdQuery
} from '../../../services/startTestAPI/startTestApi';
import { getItem, setItem } from '../../../utils/storage';
import Button from '../../Common/Button';

import Explanation from './Explanation';
import FiltersPopUp from './FiltersPopUp';
import QuestionContent from './QuestionContent';
import QuestionsLeftPane, { Category, FilterValue } from './QuestionsLeftPane';

// Types

export type ScoreModule = {
  name: string;
  score: string;
  total?: string;
  subModules?: {
    name: string;
    score: string;
  }[];
};
export type ScoreSection = {
  title: string;
  totalScore: string;
  modules: ScoreModule[];
};

const TestResults = ({ isCloudPrepTest }: { isCloudPrepTest?: boolean }) => {
  const location = useLocation();
  const [openPanels, setOpenPanels] = useState({
    rawScore: true,
    finalScore: true
  });
  const { id, userId, redirect, userMockTestId, isMockTest } = useParams();
  const params = new URLSearchParams(window.location.search);
  const tokenParam = params.get('token');
  const [showFilters, setShowFilters] = useState(false);
  const {
    isFullTestResultScreen,
    filteredData,
    filters,
    userTestId,
    resultById
  } = useAppSelector((state: RootState) => state.root.startFulltest);
  useGetTestResultByIdQuery(
    { userTestId: id ?? userTestId, userId: userId ?? '' },
    { skip: !id || isCloudPrepTest, refetchOnMountOrArgChange: true }
  );
  useFetchMockTestResulsQuery(
    { userId: userId ?? '', userTestId: userMockTestId },
    { skip: !userMockTestId, refetchOnMountOrArgChange: true }
  );
  const { data: cloudData } = useFetchCloudPrepUserTestQuery(
    { userTestId: id ?? userTestId, userId: userId ?? '' },
    { skip: !id || !isCloudPrepTest, refetchOnMountOrArgChange: true }
  );
  const { data: sectionsData, error: sectionsError } =
    useGetAllSectionsDataQuery({}, { refetchOnMountOrArgChange: true });

  useEffect(() => {
    if (tokenParam) {
      setItem({ key: 'route', value: `/practices/results/${id}` });
      setItem({ key: 'token', value: atob(tokenParam) });
      setItem({ key: 'isLogin', value: 'true' });
      const decoded: any = jwtDecode(atob(tokenParam));
      setItem({ key: 'userName', value: decoded?.firstName });
      setItem({ key: 'userRole', value: decoded?.role ?? null });
      setItem({ key: 'userEmail', value: decoded?.userEmail });
      const expiryTime = decoded.exp;
      if (expiryTime) {
        setItem({ key: 'expiryTime', value: expiryTime.toString() });
      }
    }
  }, [id, tokenParam]);

  const isParent = getItem('userRole') === 'PARENT';
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setIsTestStarted(false));
    dispatch(setIsTestInstructionsOpen(false));
  }, [dispatch]);

  const [getTestResultById, { error, isError }] =
    useLazyGetTestResultByIdQuery();
  const [fetchFullTestDetails] = useLazyGetWrittenTestFullDetailsQuery();

  const hasFullTestDetails = Boolean(
    resultById?.testDTO?.examPatternDTO?.rounds?.[0]?.sections?.length
  );

  useEffect(() => {
    if (resultById?.id && !hasFullTestDetails && !isCloudPrepTest) {
      fetchFullTestDetails({ userTestId: resultById.id, userId })
        .unwrap()
        .then(response => {
          dispatch(setResultByIdTestDTO(response?.data));
          dispatch(setTestDetailsUnavailable(false));
        })
        .catch(err => {
          console.error('Error fetching full test details:', err);
          dispatch(setTestDetailsUnavailable(true));
        });
    }
  }, [resultById?.id]);

  const navigate = useNavigate();
  const role = getItem('userRole');

  const togglePanel = (panel: keyof typeof openPanels) => {
    setOpenPanels({ ...openPanels, [panel]: !openPanels[panel] });
  };

  useEffect(() => {
    if (error && 'status' in error) {
      if (error.status === 400) {
        if (role === 'STUDENT') {
          navigate('/dashboard');
        } else {
          navigate('/parentdashboard');
        }
      } else if (error.status === 401) {
        navigate('/login');
      }
    }
  }, [error, role]);

  const handleClickExit = () => {
    dispatch(setQid(''));
    dispatch(setCurrentQuestion(0));
    dispatch(setResetFilters());

    if (isParent) {
      navigate('/parentdashboard');
      return;
    }

    const path = location.pathname;

    if (path.includes('cloud-prep') && redirect === 'true') {
      navigate('/cloud-prep');
    } else if (path.includes('results')) {
      navigate('/results');
    } else {
      navigate(-1);
    }
  };

  const toggleFilter = (category: Category, value: FilterValue) => {
    const newValue =
      !filters[category][value as keyof (typeof filters)[Category]];
    const categoryValues = Object.values(filters[category]);
    if (!newValue && categoryValues.filter(Boolean).length === 1) return;
    dispatch(
      setFilters({
        ...filters,
        [category]: {
          ...filters[category],
          [value]: !filters[category][value as keyof (typeof filters)[Category]]
        }
      })
    );
  };

  return (
    <>
      {/* <Header route={istokenParam ? `/practices/results/${id}` : ''} /> */}
      <div className='font-gully mx-auto mb-4 mt-8 h-full w-full overflow-auto rounded-lg bg-white px-4'>
        {isError ? (
          <h1> isError </h1>
        ) : (
          <div className='mb-16 grid h-screen grid-cols-[auto,1fr] gap-2'>
            <div className='relative'>
              {!isFullTestResultScreen && (
                <QuestionsLeftPane
                  setShowFilters={setShowFilters}
                  showFilters={showFilters}
                />
              )}
              {/* Filters Dropdown */}
              {showFilters && (
                <FiltersPopUp
                  toggleFilter={toggleFilter}
                  showFilters={showFilters}
                  setShowFilters={setShowFilters}
                />
              )}
            </div>
            <div className={'flex flex-grow flex-col'}>
              {!isFullTestResultScreen && (
                <div className='mb-2 flex items-center justify-between gap-2'>
                  <h1 className='px-1 text-lg'>
                    Total Score :{' '}
                    <span className='font-bold'>
                      {resultById?.totalQuestionsCorrect}/
                      {resultById?.totalQuestionsGiven}
                    </span>
                  </h1>
                  {/* Raw Score Card */}
                  {/* <RawScoreCard
                    togglePanel={panel =>
                      togglePanel(panel as 'rawScore' | 'finalScore')
                    }
                    openPanels={openPanels}
                  /> */}

                  {/* Final Score Card */}
                  {/* <FinalScoreCard
                    togglePanel={panel =>
                      togglePanel(panel as 'rawScore' | 'finalScore')
                    }
                    openPanels={openPanels}
                  /> */}

                  {!isMockTest && (
                    <div className='basis-[10%]'>
                      <Button
                        variant='contained'
                        label='Exit'
                        handleClickBtn={handleClickExit}
                        classes='h-[45px] px-6 text-[21px] font-bold text-white'
                      />
                    </div>
                  )}
                </div>
              )}
              <div
                style={{ boxShadow: '0px 0px 10px 5px #20202026' }}
                className='mb-2 w-full rounded-xl px-6 pb-2 pt-6'
              >
                <QuestionContent isCloudPrepTest={isCloudPrepTest} />
              </div>
              <div
                style={{ boxShadow: '0px 0px 10px 5px #20202026' }}
                className='mb-10 rounded-xl p-6'
              >
                <Explanation />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TestResults;
